import React, { useEffect, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { useOptions } from '../../../hooks/useCrmData';
import { getAccessToken } from '../../../authUtils';
import {
  Modal, FormField, TextInput, Select, Btn, ErrorBlock,
} from '../../ui/crm-ui';

interface Props {
  baseApiUrl: string;
  open: boolean;
  /** The group_id this job will hang from (corp or family). */
  groupId: number;
  groupName?: string;
  onCreated: (newJobId: number) => void;
  onClose: () => void;
}

function apiError(err: unknown): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as { error?: string } | undefined;
    return data?.error || err.message;
  }
  return err instanceof Error ? err.message : String(err);
}

const STATUS_OPTIONS: Array<{ label: string; value: 'active' | 'completed' | 'on_hold' }> = [
  { label: 'Active', value: 'active' },
  { label: 'Completed', value: 'completed' },
  { label: 'On Hold', value: 'on_hold' },
];

const CreateJobModal: React.FC<Props> = ({ baseApiUrl, open, groupId, groupName, onCreated, onClose }) => {
  const [name, setName] = useState('');
  const [year, setYear] = useState<string>(String(new Date().getFullYear()));
  const [jobTypeId, setJobTypeId] = useState<string>('');
  const [status, setStatus] = useState<'active' | 'completed' | 'on_hold'>('active');
  const [departmentId, setDepartmentId] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName('');
    setYear(String(new Date().getFullYear()));
    setJobTypeId('');
    setStatus('active');
    setDepartmentId('');
    setStartDate('');
    setEndDate('');
    setSubmitting(false);
    setError(null);
  }, [open]);

  const jobTypes = useOptions(baseApiUrl, open ? 'job_types' : null);
  const departments = useOptions(baseApiUrl, open ? 'departments' : null);

  async function handleSubmit() {
    setError(null);
    if (!name.trim()) { setError('Name is required.'); return; }
    const token = await getAccessToken();
    if (!token) { setError('Not authenticated. Please sign in and try again.'); return; }
    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        name: name.trim(),
        group_id: groupId,
        status,
      };
      if (year) {
        const y = Number(year);
        if (!Number.isInteger(y) || y < 1900 || y > 3000) {
          setError('Year must be a valid 4-digit year.');
          setSubmitting(false);
          return;
        }
        payload.year = y;
      }
      if (jobTypeId) payload.job_type_id = Number(jobTypeId);
      if (departmentId) payload.department_id = Number(departmentId);
      if (startDate) payload.start_date = startDate;
      if (endDate) payload.end_date = endDate;

      const res = await axios.post<{ id: number }>(`${baseApiUrl}/api/job-records`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onCreated(res.data.id);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={submitting ? () => undefined : onClose}
      title={groupName ? `Add Job — ${groupName}` : 'Add Job'}
      footer={
        <>
          <Btn onClick={onClose} disabled={submitting}>Cancel</Btn>
          <Btn primary onClick={handleSubmit} disabled={submitting || !name.trim()}>
            {submitting ? 'Creating…' : 'Create'}
          </Btn>
        </>
      }
    >
      <FormField label="Name" required>
        <TextInput value={name} onChange={setName} placeholder="e.g. Year End 2026" autoFocus />
      </FormField>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FormField label="Year">
          <TextInput value={year} onChange={setYear} placeholder="2026" type="number" />
        </FormField>
        <FormField label="Status">
          <Select
            value={status}
            onChange={(v) => setStatus(v as 'active' | 'completed' | 'on_hold')}
            options={STATUS_OPTIONS}
          />
        </FormField>
      </div>

      <FormField label="Job Type">
        <Select
          value={jobTypeId}
          onChange={setJobTypeId}
          options={(jobTypes.data || []).map((t) => ({ label: t.name, value: t.id }))}
          placeholder="(none)"
          disabled={jobTypes.loading}
        />
      </FormField>

      <FormField label="Department">
        <Select
          value={departmentId}
          onChange={setDepartmentId}
          options={(departments.data || []).map((d) => ({ label: d.name, value: d.id }))}
          placeholder="(none)"
          disabled={departments.loading}
        />
      </FormField>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FormField label="Start date">
          <TextInput value={startDate} onChange={setStartDate} type="date" />
        </FormField>
        <FormField label="End date">
          <TextInput value={endDate} onChange={setEndDate} type="date" />
        </FormField>
      </div>

      {error && <div style={{ marginTop: 12 }}><ErrorBlock message={error} /></div>}
    </Modal>
  );
};

export default CreateJobModal;
